import isEmpty from "lodash/isEmpty";
import { isValidCron } from "cron-validator";
import { $t } from "@/plugins/i18n";

const rules = {
  /** 필수값 */
  required: (v) => {
    // use it when value is object
    if (typeof v === "object" && isEmpty(v))
      return $t("common.base.label.required");

    return !!v || $t("common.base.label.required");
  },
  /** 비밀번호 */
  password: (v) => {
    const reg = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
    if (!v) {
      return $t("common.base.rule_message.password_required");
    }
    if (v.length < 6) {
      return $t("common.base.rule_message.min_length_6");
    }
    if (!reg.test(v)) {
      return $t("common.base.rule_message.password_requirements");
    }
    return true;
  },
  /** (알파벳 | 숫자 | _ | -) 허용 */
  alphanumeric: (value) => {
    const reg = /^[\w-]+$/;
    if (!reg.test(value)) {
      return $t("common.base.rule_message.alphanumeric");
    }
    return true;
  },
  /** 이름 등록 시 규칙 */
  name: (value) => {
    // # 값이 존재 해야함
    if (value == null || value.length < 1) {
      return $t("common.base.label.required");
    }

    //SKCPL-338 value == undefined일때 앞에서 안걸러지고 여기까지 넘어옴
    if (value != null && value.length > 253) {
      return $t("common.base.rule_message.max_length", { length: 253 });
    }

    // # 허용문자(영문 소문자 | 숫자 | - | .)
    if (!/^[a-z0-9-.]+$/.test(value)) {
      return $t("common.base.rule_message.lowercase_alphanumeric");
    }

    // # 시작문자, 종료 문자 제한
    const alphanumeric = /^[a-z0-9]+$/;
    if (value != null && !alphanumeric.test(value.slice(0, 1))) {
      return $t("common.base.rule_message.start_with_alphanumeric");
    }
    if (value != null && !alphanumeric.test(value.slice(-1))) {
      return $t("common.base.rule_message.end_with_alphanumeric");
    }

    return true;
  },
  /** DNS Rules(한글 허용 안함) */
  dns: (value) => {
    const reg = /[a-z]([-a-z0-9]*[a-z0-9])?/;
    if (typeof value !== "string") {
      return $t("common.base.label.required");
    }
    // // # 2자 이상
    // if (value.length < 2) {
    //   return "Minimum of 2 characters";
    // }

    // // # 63자 이하
    // if (value.length > 63) {
    //   return "Maximum of 63 characters";
    // }
    // console.log(reg.test(value));
    // # dns명 검사
    if (!reg.test(value)) {
      return $t("common.base.rule_message.invalid_dns");
    }

    return true;
  },
  /** 이메일 규칙 */
  email: (value) => {
    var reg =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return reg.test(value) == true
      ? true
      : $t("common.base.rule_message.wrong_email_format");
  },
  /** 도메인 규칙 */
  domain: (value) => {
    // https://stackoverflow.com/a/26987741
    const reg =
      /^(((?!-))(xn--|_{1,1})?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9\-]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/;
    return reg.test(value) || $t("common.base.rule_message.invalid_domain");
  },
  /** url 규칙 */
  url: (value) => {
    if (!value) return true;
    const reg =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=](?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;
    if (!reg.test(value)) {
      return $t("common.base.rule_message.wrong_url");
    }
    return true;
  },
  /** git url 규칙 */
  gitUrl: (value) => {
    const reg =
      /(?:git|ssh|https?|git@[-\w.]+):(\/\/)?(.*?)(\.git)(\/?|#[-\d\w._]+?)$/;
    return reg.test(value) || $t("common.base.rule_message.invalid_url");
  },
  /** 시간 문자열 입력 규칙(`01` 같이 처음이 0으로 시작하는 문자 허용) */
  hour: (value) => {
    const reg = /^((0)|([0-1])?([0-9])|(2[0-3]))$/;
    return reg.test(value) || $t("common.base.rule_message.hour_range");
  },
  /** 분 문자열 입력 규칙(`01` 같이 처음이 0으로 시작하는 문자 허용) */
  minute: (value) => {
    const reg = /^((0)|([0-5])?([0-9]))$/;
    return reg.test(value) || $t("common.base.rule_message.minute_range");
  },
  /** 문자열 내 공백이 있을 경우 차단 */
  forbiddenBlank: (value) => {
    if (!value) {
      return true;
    }
    return (
      !value.includes(" ") ||
      $t("common.base.rule_message.whitespace_cannot_be_entered")
    );
  },
  /** 지정된 문자가 포함되어 있을 경우 차단 */
  forbiddenText: (text) => (value) => {
    if (!value) {
      return true;
    }
    return (
      !value.includes(text) ||
      $t("common.base.rule_message.cannot_be_entered", { character: text })
    );
  },
  /** 지정된 문자로 시작하거나 끝나는 경우 차단 */
  forbiddenEdge: (text) => (value) => {
    if (!value) {
      return true;
    }
    if (value.startsWith(text)) {
      return $t("common.base.rule_message.cannot_be_started_with", {
        text: text,
      });
    }
    return (
      !value.endsWith(text) ||
      $t("common.base.rule_message.cannot_be_ended_with", { text: text })
    );
  },
  /** annotation key 정규식 */
  annotationKey: (value) => {
    if (!value) {
      return true;
    }

    const v = value.split("/");
    if (v.length > 2) {
      return $t("common.base.rule_message.can_use_only_once", { text: "/" });
    }
    const prefix = v.length === 1 ? "temp" : v[0]; // prefix 미사용 시 정규식 통과를 위한 임시 값 부여
    const name = v.length === 1 ? v[0] : v[1];

    if (prefix.length > 253) {
      return $t("common.base.rule_message.max_length", { length: 253 });
    }
    if (name.length > 63) {
      return $t("common.base.rule_message.max_length", { length: 63 });
    }

    const prefixReg =
      /^([a-z0-9]([-a-z0-9]*[a-z0-9])?\.)*([a-z0-9]([-a-z0-9]*[a-z0-9])?)$/;
    const nameReg = /^[A-Za-z0-9]([A-Za-z0-9_.-]*[A-Za-z0-9])?$/;
    return (
      (prefixReg.test(prefix) && nameReg.test(name)) ||
      $t("common.base.rule_message.annotation_key_format")
    );
  },
  /** Linux Cron 정규식 */
  cronText: (value) => {
    const reg =
      /^(\*|[0-9\-\,]+)\s(\*|[0-9\-\,]+)\s(\*|[0-9\-\,]+)\s(\*|[0-9\-\,]+)\s(\*|[0-9\-\,]+)$/;
    return (
      reg.test(value) || $t("common.base.rule_message.invalid_cron_expression")
    );
  },
  prefix_task: (value) => {
    const regex = /^\[[0-9a-zA-Z]{0,13}\]$/;
    if (value && !regex.test(value)) {
      return $t("common.base.rule_message.prefix_task_format");
    }

    return true;
  },
  cron_job_general: (value) => {
    return (
      isValidCron(value || "", {
        seconds: true,
        alias: true,
        allowSevenAsSunday: true,
      }) || $t("common.base.rule_message.cron_regex_not_correct")
    );
  },
  maxLength(max) {
    return (v) => {
      if (!v) return true;
      return (
        v.length <= max ||
        $t("common.base.rule_message.max_length", { length: max })
      );
    };
  },
};

export const RulesMixin = {
  data: () => ({
    rules,
  }),
};
